import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { newLogo } from "../assets";
import {
  useGetMerchantBusinesses,
  useGetSubmerchantBusinesses,
} from "../api/services/businessServices";
import { useVerifyUser } from "../api/services/authServices";
import { logout } from "../redux/slices/authSlice";
import Loader from "../components/Loader";

const WelcomeLayout = ({ children }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  useVerifyUser(pathname);

  const isMerchant = user?.type?.toLowerCase() === "merchant";

  const merchantBusinesses = useGetMerchantBusinesses(user?.id, {}, isMerchant);
  const submerchantBusinesses = useGetSubmerchantBusinesses(user?.id, !isMerchant);

  const { data: userBusinesses, isPending: isLoading } = isMerchant
    ? merchantBusinesses
    : submerchantBusinesses;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-4">
        <Loader size="lg" text="" />
        <p className="text-gray-500 text-sm font-medium animate-pulse">
          Getting things ready...
        </p>
      </div>
    );
  }

  if (Array.isArray(userBusinesses?.data) && userBusinesses.data.length > 0) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="bg-white">
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg overflow-hidden shadow-sm shadow-primary/10 ring-1 ring-black/5 flex items-center justify-center">
              <img
                src={newLogo}
                alt="ZApp"
                className="h-full w-full object-cover"
              />
            </div>
            <div className="hidden sm:block leading-tight">
              <p className="text-sm font-extrabold text-gray-900">ZApp</p>
              <p className="text-[11px] text-gray-500 font-medium">Merchant Portal</p>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-1">
            {[
              { label: "Features", id: "features" },
              { label: "How It Works", id: "how-it-works" },
              { label: "Business Types", id: "business-types" },
            ].map(({ label, id }) => (
              <button
                key={id}
                onClick={() => scrollTo(id)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-primary hover:bg-primary/5 rounded-lg transition-colors font-medium"
              >
                {label}
              </button>
            ))}
          </nav>
          <button
            onClick={handleLogout}
            className="text-sm font-medium text-gray-600 hover:text-primary transition-colors px-4 py-2 rounded-lg border border-gray-200 hover:border-primary/30 hover:bg-primary/5"
          >
            Logout
          </button>
        </div>
      </header>
      <main>{children}</main>
    </div>
  );
};

export default WelcomeLayout;
